/**
 * Users API Client
 *
 * API client for user endpoints
 * Base URL: https://linkeddog-api.up.railway.app
 */

import { apiClient } from './client';

/**
 * User response from GET /users/me
 * Based on API_GUIDE.md specification
 */
export interface UserResponse {
  id: string;
  name: string;
  email: string;
  role: 'DOG_OWNER' | 'VETERINARIAN';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  image: string | null;
  phoneNumber: string | null;
  emailVerified: boolean;
  phoneNumberVerified: boolean | null;
  veterinarianId: string | null;
  hasCompletedOnboarding: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

export interface UpdateUserRequest {
  name?: string;
  phoneNumber?: string | null;
  image?: string | null;
}

export const usersApi = {
  /**
   * Get current user data
   * Endpoint: GET /users/me
   *
   * @returns User data including hasCompletedOnboarding flag
   */
  me: async (): Promise<UserResponse> => {
    const response = await apiClient.get<UserResponse>('/users/me');
    return response.data;
  },

  /**
   * Update current user data
   * Endpoint: PUT /users/me
   *
   * @returns Updated user data
   */
  update: async (data: UpdateUserRequest): Promise<UserResponse> => {
    const response = await apiClient.put<UserResponse>('/users/me', data);
    return response.data;
  },

  /**
   * Mark user onboarding as complete
   * Endpoint: POST /users/complete-onboarding
   *
   * @returns 204 No Content on success
   */
  completeOnboarding: async (): Promise<void> => {
    await apiClient.post('/users/complete-onboarding');
  },
};
